/**
 * TotalKlickADS · Brand Settings — Integrations (Meta, Canva)
 * Route: /totalklickads/brand/:brandId/settings
 *
 * Configurazione per-brand delle credenziali per:
 *  - Meta (Facebook + Instagram Graph API)
 *  - Canva Connect API
 *
 * Ogni utente compila le proprie credenziali (NON globali). Secrets
 * cifrati at rest con Fernet sul backend.
 */
import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
    ArrowLeft, ShieldCheck, AlertCircle, ExternalLink, Trash2,
    CheckCircle2, XCircle, Loader2, Eye, EyeOff, Save, KeyRound,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import ModuleAccent from "@/components/ModuleAccent";

const PRIMARY = "#7C3AED";
const ACCENT = "#FF1A5C";

export default function TotalKlickADSSettings() {
    const { brandId } = useParams();
    const [brand, setBrand] = useState(null);
    const [activeTab, setActiveTab] = useState("meta");
    const [error, setError] = useState("");

    useEffect(() => {
        api.get(`/ads/brands/${brandId}`).then(r => setBrand(r.data.brand))
            .catch(e => setError(formatApiError(e)));
    }, [brandId]);

    return (
        <ModuleAccent module="ads" as="div" style={{ fontFamily: '"Plus Jakarta Sans", system-ui, sans-serif', background: "#0A1024", color: "#fff", minHeight: "100vh" }} data-testid="ads-settings-page">
            {/* Topbar */}
            <header className="px-6 md:px-10 py-3.5 flex items-center justify-between sticky top-0 z-40 backdrop-blur-md" style={{ background: "rgba(10,16,36,0.92)", borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
                <div className="flex items-center gap-6">
                    <Link to="/totalklickads/dashboard" className="flex items-center gap-2">
                        <img src="/brand/totalklickads/logo.png" alt="TotalKlickADS" className="h-7" />
                    </Link>
                    <nav className="hidden md:flex items-center gap-1 text-[12.5px] font-bold">
                        <Link to="/totalklickads/dashboard" className="px-3 py-1.5 opacity-60 hover:opacity-100">Brand</Link>
                        <Link to={`/totalklickads/brand/${brandId}/calendar`} className="px-3 py-1.5 opacity-60 hover:opacity-100">Calendario</Link>
                        <span className="px-3 py-1.5 rounded-full" style={{ background: "rgba(124,58,237,0.18)", color: "#C4B5FD" }}>Integrazioni</span>
                    </nav>
                </div>
                <Link to={`/totalklickads/brand/${brandId}`} className="text-[12px] opacity-60 hover:opacity-100 inline-flex items-center gap-1"><ArrowLeft size={12} /> Brand</Link>
            </header>

            {/* Hero */}
            <section className="px-6 md:px-10 py-7 border-b" style={{ borderColor: "rgba(255,255,255,0.06)" }}>
                <div className="max-w-4xl mx-auto">
                    <div className="text-[10.5px] mono uppercase tracking-[0.25em] opacity-50 mb-1">{brand?.name || "..."}</div>
                    <h1 className="text-[26px] md:text-[32px] font-extrabold tracking-tighter inline-flex items-center gap-2">
                        <KeyRound size={22} style={{ color: PRIMARY }} /> Integrazioni
                    </h1>
                    <p className="text-[13px] opacity-60 mt-1 max-w-xl">
                        Collega gli account dei tuoi servizi esterni. Le credenziali sono cifrate (Fernet AES-128) e salvate solo sul server, mai esposte al frontend.
                    </p>
                </div>
            </section>

            {/* Tabs */}
            <section className="px-6 md:px-10 pt-6">
                <div className="max-w-4xl mx-auto">
                    <div className="inline-flex rounded-full p-1" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }}>
                        <button onClick={() => setActiveTab("meta")}
                            className="px-4 py-1.5 rounded-full text-[12px] font-bold transition"
                            style={{ background: activeTab === "meta" ? "linear-gradient(135deg,#1877F2,#0866FF)" : "transparent", color: activeTab === "meta" ? "#FFF" : "rgba(255,255,255,0.5)" }}
                            data-testid="ads-settings-tab-meta">
                            Meta (Instagram + Facebook)
                        </button>
                        <button onClick={() => setActiveTab("canva")}
                            className="px-4 py-1.5 rounded-full text-[12px] font-bold transition"
                            style={{ background: activeTab === "canva" ? "linear-gradient(135deg,#00C4CC,#7D2AE7)" : "transparent", color: activeTab === "canva" ? "#FFF" : "rgba(255,255,255,0.5)" }}
                            data-testid="ads-settings-tab-canva">
                            Canva Connect
                        </button>
                    </div>
                </div>
            </section>

            {error && (
                <div className="max-w-4xl mx-auto px-6 md:px-10 mt-4">
                    <div className="p-3 rounded-lg text-[13px] inline-flex items-center gap-2" style={{ background: "rgba(239,68,68,0.12)", color: "#FCA5A5" }}>
                        <AlertCircle size={14} /> {error}
                    </div>
                </div>
            )}

            <section className="px-6 md:px-10 py-6 pb-20">
                <div className="max-w-4xl mx-auto">
                    {activeTab === "meta" ? <MetaSettings brandId={brandId} /> : <CanvaSettings brandId={brandId} />}
                </div>
            </section>
        </ModuleAccent>
    );
}

/* =====================================================================
 * META settings panel
 * ===================================================================== */
function MetaSettings({ brandId }) {
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState({ connected: false });
    const [form, setForm] = useState({ app_id: "", app_secret: "", page_id: "", instagram_business_account_id: "", page_access_token: "" });
    const [showSecrets, setShowSecrets] = useState(false);
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [testResult, setTestResult] = useState(null);
    const [err, setErr] = useState("");

    const load = async () => {
        setLoading(true);
        try {
            const r = await api.get(`/ads/brands/${brandId}/integrations/meta`);
            setData(r.data || { connected: false });
            setForm(prev => ({
                ...prev,
                app_id: r.data?.app_id || "",
                page_id: r.data?.page_id || "",
                instagram_business_account_id: r.data?.instagram_business_account_id || "",
            }));
        } catch (e) { setErr(formatApiError(e)); }
        setLoading(false);
    };
    useEffect(() => { load(); }, [brandId]);  // eslint-disable-line

    const onSave = async (e) => {
        e.preventDefault();
        setSaving(true); setErr(""); setTestResult(null);
        try {
            const body = { app_id: form.app_id, page_id: form.page_id, instagram_business_account_id: form.instagram_business_account_id };
            if (form.app_secret) body.app_secret = form.app_secret;
            if (form.page_access_token) body.page_access_token = form.page_access_token;
            await api.put(`/ads/brands/${brandId}/integrations/meta`, body);
            setForm(p => ({ ...p, app_secret: "", page_access_token: "" }));
            await load();
        } catch (e) { setErr(formatApiError(e)); }
        setSaving(false);
    };

    const onTest = async () => {
        setTesting(true); setTestResult(null); setErr("");
        try {
            const r = await api.post(`/ads/brands/${brandId}/integrations/meta/test`);
            setTestResult({ ok: true, data: r.data });
            await load();
        } catch (e) {
            setTestResult({ ok: false, message: formatApiError(e) });
        }
        setTesting(false);
    };

    const onDisconnect = async () => {
        if (!window.confirm("Disconnettere Meta? Le credenziali verranno cancellate.")) return;
        try {
            await api.delete(`/ads/brands/${brandId}/integrations/meta`);
            setForm({ app_id: "", app_secret: "", page_id: "", instagram_business_account_id: "", page_access_token: "" });
            await load();
        } catch (e) { setErr(formatApiError(e)); }
    };

    return (
        <div className="space-y-6">
            {/* Status header */}
            <div className="rounded-2xl p-5 flex items-center justify-between" style={{ background: data.connected ? "rgba(52,211,153,0.06)" : "rgba(255,255,255,0.03)", border: `1px solid ${data.connected ? "rgba(52,211,153,0.3)" : "rgba(255,255,255,0.08)"}` }}>
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl grid place-items-center" style={{ background: "linear-gradient(135deg,#1877F2,#0866FF)" }}>
                        <span className="font-extrabold text-[18px]">f</span>
                    </div>
                    <div>
                        <div className="font-extrabold text-[15px]">Meta · Instagram + Facebook</div>
                        <div className="text-[12px] opacity-60 inline-flex items-center gap-1.5 mt-0.5">
                            {data.connected ? (<><CheckCircle2 size={12} style={{ color: "#34D399" }} /> Connesso</>) : (<><XCircle size={12} style={{ color: "#9CA3AF" }} /> Non connesso</>)}
                            {data.last_test_at && <span className="opacity-50">· verificato {new Date(data.last_test_at).toLocaleString("it-IT")}</span>}
                        </div>
                    </div>
                </div>
                {data.connected && (
                    <button onClick={onDisconnect} className="text-[12px] opacity-60 hover:opacity-100 inline-flex items-center gap-1" data-testid="ads-meta-disconnect">
                        <Trash2 size={12} /> Disconnetti
                    </button>
                )}
            </div>

            {/* Guide */}
            <details className="rounded-2xl p-5" style={{ background: "rgba(24,119,242,0.04)", border: "1px solid rgba(24,119,242,0.15)" }}>
                <summary className="cursor-pointer font-extrabold text-[14px] inline-flex items-center gap-2">
                    <ShieldCheck size={14} style={{ color: "#60A5FA" }} /> Come ottenere le credenziali (guida step-by-step)
                </summary>
                <div className="text-[12.5px] opacity-90 leading-relaxed space-y-2 mt-3">
                    <p><b>1.</b> Vai su <a className="underline" target="_blank" rel="noreferrer" href="https://developers.facebook.com/apps/">developers.facebook.com</a> e crea un'<b>App</b> (tipo Business).</p>
                    <p><b>2.</b> Nella dashboard dell'app, aggiungi i prodotti <b>Facebook Login for Business</b> e <b>Instagram Graph API</b>.</p>
                    <p><b>3.</b> Configura i permessi: <code className="opacity-90">pages_manage_posts</code>, <code className="opacity-90">pages_read_engagement</code>, <code className="opacity-90">pages_show_list</code>, <code className="opacity-90">instagram_basic</code>, <code className="opacity-90">instagram_content_publish</code>.</p>
                    <p><b>4.</b> Da Impostazioni → Base copia <b>App ID</b> e <b>App Secret</b>.</p>
                    <p><b>5.</b> Il tuo account Instagram deve essere <b>Business</b> e collegato a una <b>Pagina Facebook</b> di cui sei admin (dall'app IG: Impostazioni → Account → Tipo di account).</p>
                    <p><b>6.</b> Usa il <a className="underline" target="_blank" rel="noreferrer" href="https://developers.facebook.com/tools/explorer/">Graph API Explorer</a> per:</p>
                    <ul className="list-disc list-inside ml-2 opacity-80">
                        <li>Genera un <b>User Access Token</b> con i permessi sopra</li>
                        <li>Chiama <code>/me/accounts</code> → trovi <b>Page ID</b> e <b>Page Access Token</b></li>
                        <li>Chiama <code>/&#123;page-id&#125;?fields=instagram_business_account</code> → trovi <b>Instagram Business Account ID</b></li>
                        <li>Scambia il Page Access Token con uno <b>long-lived</b> via <code>/oauth/access_token</code></li>
                    </ul>
                    <p className="text-[11.5px] opacity-60 mt-2">📌 Nota: il publishing diretto delle <b>Stories Instagram</b> non è supportato dall'API ufficiale. Solo Feed (Foto) + Reels (Video).</p>
                </div>
            </details>

            {/* Form */}
            <form onSubmit={onSave} className="rounded-2xl p-5 space-y-4" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)" }}>
                <div>
                    <label className="text-[10.5px] mono uppercase tracking-wider opacity-60">App ID</label>
                    <input value={form.app_id} onChange={e => setForm({ ...form, app_id: e.target.value })}
                        className="w-full mt-1 bg-transparent text-[13px] mono px-3 py-2 rounded-lg outline-none"
                        style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }}
                        placeholder="es. 7295041520..." data-testid="ads-meta-app-id" required />
                </div>
                <SecretInput label="App Secret" value={form.app_secret} onChange={v => setForm({ ...form, app_secret: v })}
                    placeholder={data.has_app_secret ? "•••••••• (lasciare vuoto per non modificare)" : "es. a1b2c3d4..."}
                    show={showSecrets} testid="ads-meta-app-secret" />
                <div>
                    <label className="text-[10.5px] mono uppercase tracking-wider opacity-60">Facebook Page ID</label>
                    <input value={form.page_id} onChange={e => setForm({ ...form, page_id: e.target.value })}
                        className="w-full mt-1 bg-transparent text-[13px] mono px-3 py-2 rounded-lg outline-none"
                        style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }}
                        placeholder="es. 1234567890" data-testid="ads-meta-page-id" required />
                </div>
                <div>
                    <label className="text-[10.5px] mono uppercase tracking-wider opacity-60">Instagram Business Account ID</label>
                    <input value={form.instagram_business_account_id} onChange={e => setForm({ ...form, instagram_business_account_id: e.target.value })}
                        className="w-full mt-1 bg-transparent text-[13px] mono px-3 py-2 rounded-lg outline-none"
                        style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }}
                        placeholder="es. 17841401234..." data-testid="ads-meta-ig-id" />
                </div>
                <SecretInput label="Page Access Token (long-lived)" value={form.page_access_token} onChange={v => setForm({ ...form, page_access_token: v })}
                    placeholder={data.has_page_access_token ? "•••••••• (lasciare vuoto per non modificare)" : "EAA..."}
                    show={showSecrets} testid="ads-meta-page-token" multiline />
                <div className="flex items-center justify-between flex-wrap gap-2">
                    <button type="button" onClick={() => setShowSecrets(s => !s)}
                        className="text-[11.5px] opacity-60 hover:opacity-100 inline-flex items-center gap-1">
                        {showSecrets ? <EyeOff size={12} /> : <Eye size={12} />}
                        {showSecrets ? "Nascondi secrets" : "Mostra secrets in chiaro"}
                    </button>
                    <div className="flex items-center gap-2">
                        <button type="button" onClick={onTest} disabled={testing || (!data.has_page_access_token && !form.page_access_token)}
                            className="px-4 py-2 rounded-full text-[12px] font-bold inline-flex items-center gap-1.5 disabled:opacity-50"
                            style={{ background: "rgba(96,165,250,0.15)", color: "#60A5FA", border: "1px solid rgba(96,165,250,0.3)" }}
                            data-testid="ads-meta-test"
                            title={!data.has_page_access_token && !form.page_access_token ? "Salva prima un Page Access Token" : "Chiama Graph API per verificare"}>
                            {testing ? (<><Loader2 size={12} className="animate-spin" /> Verifica...</>) : "Verifica connessione"}
                        </button>
                        <button type="submit" disabled={saving}
                            className="px-5 py-2 rounded-full text-[12px] font-extrabold text-white inline-flex items-center gap-1.5 disabled:opacity-50"
                            style={{ background: "linear-gradient(135deg,#1877F2,#0866FF)" }}
                            data-testid="ads-meta-save">
                            <Save size={12} /> {saving ? "Salvataggio..." : "Salva"}
                        </button>
                    </div>
                </div>
                {testResult && (
                    <div className={`p-3 rounded-lg text-[12.5px] ${testResult.ok ? "" : ""}`}
                        style={testResult.ok ? { background: "rgba(52,211,153,0.1)", color: "#A7F3D0" } : { background: "rgba(239,68,68,0.1)", color: "#FCA5A5" }}>
                        {testResult.ok ? (
                            <><b>Connesso!</b> Pagina: <b>{testResult.data?.page_name || "?"}</b>. IG Business: <code>{testResult.data?.instagram_business_account || "—"}</code></>
                        ) : (<>❌ {testResult.message}</>)}
                    </div>
                )}
                {err && <div className="text-[12px] px-3 py-2 rounded-lg" style={{ background: "rgba(239,68,68,0.12)", color: "#FCA5A5" }}>{err}</div>}
            </form>
            {loading && <div className="opacity-60 py-4 text-center text-[12px]">Caricamento...</div>}
        </div>
    );
}

/* =====================================================================
 * CANVA settings panel
 * ===================================================================== */
function CanvaSettings({ brandId }) {
    const [data, setData] = useState({ connected: false });
    const [form, setForm] = useState({ client_id: "", client_secret: "", refresh_token: "" });
    const [showSecrets, setShowSecrets] = useState(false);
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState("");

    const load = async () => {
        try {
            const r = await api.get(`/ads/brands/${brandId}/integrations/canva`);
            setData(r.data || { connected: false });
            setForm(p => ({ ...p, client_id: r.data?.client_id || "" }));
        } catch (e) { setErr(formatApiError(e)); }
    };
    useEffect(() => { load(); }, [brandId]);  // eslint-disable-line

    const onSave = async (e) => {
        e.preventDefault();
        setSaving(true); setErr("");
        try {
            const body = { client_id: form.client_id };
            if (form.client_secret) body.client_secret = form.client_secret;
            if (form.refresh_token) body.refresh_token = form.refresh_token;
            await api.put(`/ads/brands/${brandId}/integrations/canva`, body);
            setForm(p => ({ ...p, client_secret: "", refresh_token: "" }));
            await load();
        } catch (e) { setErr(formatApiError(e)); }
        setSaving(false);
    };

    const onDisconnect = async () => {
        if (!window.confirm("Disconnettere Canva?")) return;
        try {
            await api.delete(`/ads/brands/${brandId}/integrations/canva`);
            setForm({ client_id: "", client_secret: "", refresh_token: "" });
            await load();
        } catch (e) { setErr(formatApiError(e)); }
    };

    return (
        <div className="space-y-6">
            <div className="rounded-2xl p-5 flex items-center justify-between" style={{ background: data.connected ? "rgba(52,211,153,0.06)" : "rgba(255,255,255,0.03)", border: `1px solid ${data.connected ? "rgba(52,211,153,0.3)" : "rgba(255,255,255,0.08)"}` }}>
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl grid place-items-center" style={{ background: "linear-gradient(135deg,#00C4CC,#7D2AE7)" }}>
                        <span className="font-extrabold text-[14px]">Cv</span>
                    </div>
                    <div>
                        <div className="font-extrabold text-[15px]">Canva Connect</div>
                        <div className="text-[12px] opacity-60 inline-flex items-center gap-1.5 mt-0.5">
                            {data.connected ? (<><CheckCircle2 size={12} style={{ color: "#34D399" }} /> Connesso</>) : (<><XCircle size={12} style={{ color: "#9CA3AF" }} /> Non connesso</>)}
                        </div>
                    </div>
                </div>
                {data.connected && (
                    <button onClick={onDisconnect} className="text-[12px] opacity-60 hover:opacity-100 inline-flex items-center gap-1" data-testid="ads-canva-disconnect">
                        <Trash2 size={12} /> Disconnetti
                    </button>
                )}
            </div>

            <details className="rounded-2xl p-5" style={{ background: "rgba(0,196,204,0.05)", border: "1px solid rgba(0,196,204,0.2)" }}>
                <summary className="cursor-pointer font-extrabold text-[14px] inline-flex items-center gap-2">
                    <ShieldCheck size={14} style={{ color: "#22D3EE" }} /> Come ottenere le credenziali (guida step-by-step)
                </summary>
                <div className="text-[12.5px] opacity-90 leading-relaxed space-y-2 mt-3">
                    <p><b>1.</b> Vai su <a className="underline" target="_blank" rel="noreferrer" href="https://www.canva.com/developers/">canva.com/developers</a> e crea un account sviluppatore (gratis, ti serve un piano Canva Pro).</p>
                    <p><b>2.</b> In "Your integrations" → <b>Create an integration</b> → tipo <b>Public</b> o <b>Internal</b>.</p>
                    <p><b>3.</b> Configura il <b>Redirect URI</b> (per ora puoi usare <code>https://example.com/canva/callback</code> e cambiarlo dopo).</p>
                    <p><b>4.</b> Abilita gli <b>scope</b>:</p>
                    <ul className="list-disc list-inside ml-2 opacity-80">
                        <li><code>design:content:read</code> / <code>design:content:write</code></li>
                        <li><code>asset:read</code> / <code>asset:write</code></li>
                        <li><code>brandtemplate:meta:read</code> / <code>brandtemplate:content:read</code></li>
                    </ul>
                    <p><b>5.</b> Copia <b>Client ID</b> e <b>Client Secret</b> (il secret è visibile solo una volta!).</p>
                    <p><b>6.</b> Salva qui sotto. L'OAuth flow per generare il <b>refresh_token</b> verrà finalizzato nella prossima release (richiede HTTPS callback configurato).</p>
                    <p className="text-[11.5px] opacity-60 mt-2">📌 Una volta connesso, Aria potrà esportare i tuoi design Canva come PNG/MP4 direttamente dagli slot del piano editoriale.</p>
                </div>
            </details>

            <form onSubmit={onSave} className="rounded-2xl p-5 space-y-4" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)" }}>
                <div>
                    <label className="text-[10.5px] mono uppercase tracking-wider opacity-60">Client ID</label>
                    <input value={form.client_id} onChange={e => setForm({ ...form, client_id: e.target.value })}
                        className="w-full mt-1 bg-transparent text-[13px] mono px-3 py-2 rounded-lg outline-none"
                        style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }}
                        placeholder="OC-..." data-testid="ads-canva-client-id" required />
                </div>
                <SecretInput label="Client Secret" value={form.client_secret} onChange={v => setForm({ ...form, client_secret: v })}
                    placeholder={data.has_client_secret ? "•••••••• (lasciare vuoto per non modificare)" : "secret..."}
                    show={showSecrets} testid="ads-canva-client-secret" />
                <SecretInput label="Refresh Token (opzionale, da OAuth flow)"
                    value={form.refresh_token} onChange={v => setForm({ ...form, refresh_token: v })}
                    placeholder={data.has_refresh_token ? "•••••••• (lasciare vuoto per non modificare)" : "Generato dopo OAuth"}
                    show={showSecrets} testid="ads-canva-refresh" multiline />
                <div className="flex items-center justify-between flex-wrap gap-2">
                    <button type="button" onClick={() => setShowSecrets(s => !s)}
                        className="text-[11.5px] opacity-60 hover:opacity-100 inline-flex items-center gap-1">
                        {showSecrets ? <EyeOff size={12} /> : <Eye size={12} />}
                        {showSecrets ? "Nascondi secrets" : "Mostra secrets in chiaro"}
                    </button>
                    <button type="submit" disabled={saving}
                        className="px-5 py-2 rounded-full text-[12px] font-extrabold text-white inline-flex items-center gap-1.5 disabled:opacity-50"
                        style={{ background: "linear-gradient(135deg,#00C4CC,#7D2AE7)" }}
                        data-testid="ads-canva-save">
                        <Save size={12} /> {saving ? "Salvataggio..." : "Salva"}
                    </button>
                </div>
                {err && <div className="text-[12px] px-3 py-2 rounded-lg" style={{ background: "rgba(239,68,68,0.12)", color: "#FCA5A5" }}>{err}</div>}
            </form>
        </div>
    );
}

/* =====================================================================
 * Shared SecretInput component
 * ===================================================================== */
function SecretInput({ label, value, onChange, placeholder, show, testid, multiline }) {
    const Tag = multiline ? "textarea" : "input";
    return (
        <div>
            <label className="text-[10.5px] mono uppercase tracking-wider opacity-60">{label}</label>
            <Tag value={value} onChange={e => onChange(e.target.value)}
                type={show ? "text" : "password"}
                rows={multiline ? 3 : undefined}
                className="w-full mt-1 bg-transparent text-[13px] mono px-3 py-2 rounded-lg outline-none resize-none"
                style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", fontFamily: "JetBrains Mono, monospace" }}
                placeholder={placeholder} data-testid={testid} />
        </div>
    );
}
